import { PASSWORD_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { PasswordStrengthIndicator } from "@circle-react/components/shared/PasswordField/PasswordStrengthIndicator";
import { SecurityBadge } from "@circle-react/components/shared/Paywalls/components/SecurityBadge";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { PasswordInput } from "@circleco/compass/components/PasswordInput";
import type { FocusEvent } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { usePasswordField } from "@/react/hooks/usePasswordField";

import { EmailDisplayBox } from "./EmailDisplayBox";

interface EmailVerificationAccountSetupStateProps {
  email: string;
  onClose: () => void;
}
const LOCALE_ROOT =
  "paywall_checkout.form.email_verification_states.account_setup_state";

export const EmailVerificationAccountSetupState = ({
  email,
  onClose,
}: EmailVerificationAccountSetupStateProps) => {
  const { currentCommunity } = usePaywallCheckoutContext();
  const { register } = useFormContext();
  const { passwordValue, passwordFieldError, passwordStrength, handleBlur } =
    usePasswordField({ fieldName: "password" });
  const passwordRegistration = register("password", {
    required: true,
    pattern: PASSWORD_REGEX,
  });

  const handlePasswordBlur = async (event: FocusEvent<HTMLInputElement>) => {
    await passwordRegistration.onBlur(event);
    await handleBlur();
  };

  const communityName =
    currentCommunity?.name || t([LOCALE_ROOT, "community_label"]);

  return (
    <div className="flex flex-col gap-5 pb-6 *:px-6">
      <EmailDisplayBox email={email} onClose={onClose} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Typography.TitleSm weight="bold" size="text-md">
              {t([LOCALE_ROOT, "join_community"], {
                community_name: communityName,
              })}
            </Typography.TitleSm>
            <Typography.BodySm weight="normal" color="text-default">
              {t([LOCALE_ROOT, "create_password"], {
                community_name: communityName,
              })}
            </Typography.BodySm>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <PasswordInput
            {...passwordRegistration}
            onBlur={handlePasswordBlur}
            invalid={!!passwordFieldError}
            label={t("paywall_checkout.form.fields.password")}
            data-testid="checkout-form-password-input"
            autoFocus
          />
          <PasswordStrengthIndicator
            passwordStrength={passwordStrength}
            passwordFieldError={passwordFieldError}
            passwordValue={passwordValue}
          />
        </div>
      </div>
      <div className="flex items-center justify-start">
        <SecurityBadge />
      </div>
    </div>
  );
};
